/**
 * «в QR-папку ▾» полосы выбора «Планов» (разметка 28.09.26 11:27 п.1): новая
 * папка либо СУЩЕСТВУЮЩАЯ — из папок поставщика отмеченных партий или номером
 * руками. Запись и подтверждение — у страницы (`onNew`, `onExisting`), меню
 * только выбирает.
 *
 * Панель — `absolute` под кнопкой, как календарь «сменить дату» рядом: полоса
 * выбора стоит над таблицей, а не в виртуальном контейнере, портал не нужен.
 */

import { ChevronDown } from "lucide-react";
import { useRef, useState } from "react";

import { ApiFailure, apiFetch } from "@/lib/api";
import { qrFolderLabel, type QrFolder } from "@/lib/plans";
import { useLoad } from "@/lib/useLoad";
import { usePopover } from "@/lib/usePopover";

export function QrFolderMenu({
  why,
  busy,
  supplierId,
  onNew,
  onExisting,
}: {
  /** Почему нельзя, словами; пусто — можно. */
  why: string;
  busy: boolean;
  /** Поставщик отмеченных партий — чьи папки предлагать. */
  supplierId: number | null;
  onNew: () => void;
  onExisting: (qrId: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const [folders, setFolders] = useState<QrFolder[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [typed, setTyped] = useState("");
  const корень = useRef<HTMLSpanElement>(null);

  usePopover(open, () => setOpen(false), корень);

  // Папки читаются на КАЖДОЕ открытие: только что заведённая папка обязана
  // появиться в списке без перезагрузки экрана.
  useLoad(
    () => {
      if (!open || supplierId === null) return undefined;
      setFolders(null);
      setLoadError(null);
      return apiFetch<QrFolder[]>(`/api/plans/qr/folders?supplier_id=${supplierId}`);
    },
    [open, supplierId],
    {
      done: setFolders,
      fail: (error) =>
        setLoadError(error instanceof ApiFailure ? error.message : "папки не прочитаны: сервис не ответил"),
    },
  );

  const номер = /^\d+$/.test(typed.trim()) ? Number(typed.trim()) : null;
  const выбрать = (qrId: number) => {
    setOpen(false);
    setTyped("");
    onExisting(qrId);
  };

  return (
    <span ref={корень} className="relative inline-flex" data-annot="в QR-папку">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((on) => !on)}
        disabled={Boolean(why) || busy}
        title={why || "новая QR-папка либо дописать отмеченные партии в существующую"}
        className="flex items-center gap-0.5 font-medium text-accent-text hover:underline disabled:cursor-not-allowed disabled:text-fg-faint disabled:no-underline"
      >
        {busy ? "записываю…" : "в QR-папку"} <ChevronDown className="h-3.5 w-3.5" />
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute left-0 top-7 z-30 w-[340px] rounded-[var(--radius-card)] border border-border bg-bg-elevated py-1 text-[12.5px] shadow-lg"
          data-annot="меню QR-папки"
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onNew();
            }}
            title="создать QR-папку в CRM — тем же методом, что «+QR Быстро»"
            className="flex h-7 w-full items-center px-3 text-left font-medium hover:bg-bg-hover"
          >
            новая папка
          </button>
          <div className="mt-1 border-t border-border px-3 pb-1 pt-2 text-fg-faint">
            дописать в существующую
          </div>
          {loadError ? (
            <div className="px-3 py-1 text-err">{loadError}</div>
          ) : folders === null ? (
            <div className="px-3 py-1 text-fg-faint">читаю папки поставщика…</div>
          ) : folders.length === 0 ? (
            <div className="px-3 py-1 text-fg-faint">у поставщика папок нет</div>
          ) : (
            <div className="max-h-[260px] overflow-y-auto">
              {folders.map((f) => (
                <button
                  key={f.qr_id}
                  type="button"
                  role="menuitem"
                  onClick={() => выбрать(f.qr_id)}
                  className="flex h-7 w-full items-center truncate px-3 text-left tabular-nums hover:bg-bg-hover"
                >
                  {qrFolderLabel(f)}
                </button>
              ))}
            </div>
          )}
          <form
            className="flex items-center gap-2 border-t border-border px-3 pb-1 pt-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (номер) выбрать(номер);
            }}
          >
            <input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              inputMode="numeric"
              placeholder="номер папки"
              aria-label="номер QR-папки"
              className="h-7 w-[120px] rounded-[var(--radius-ui)] border border-border-strong bg-bg px-2 tabular-nums"
            />
            <button
              type="submit"
              disabled={!номер}
              className="font-medium text-accent-text hover:underline disabled:cursor-not-allowed disabled:text-fg-faint disabled:no-underline"
            >
              дописать
            </button>
          </form>
        </div>
      ) : null}
    </span>
  );
}
